import { useTranslation } from "react-i18next";
import { CustomTypography } from "../../../components/CustomTypography/CustomTypography";
import useStyles from "./styles";
import { Sidebar } from "../../../containers/Sidebar/Sidebar";
import { CustomButtonTable } from "../../../components/CustomButtonTable/CustomButtonTable";
import { Icon } from "../../../components/Icon/Icon";
import { ContainerCollaborator } from "../../../containers/ContainerCollaborator/ContainerCollaborator";
import { DocumentsCollaborator } from "./DocumentsCollaborator/DocumentsCollaborator";
import { CustomTable } from "../../../components/CustomTable/CustomTable";

import { VehiculeAffected } from "./VehiculeAffected/VehiculeAffected";
import { HistoriqueContent } from "./HistoriqueContent/HistoriqueContent";
import { CustomModal } from "../../../components/CustomModal/CustomModal";
import { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router";
import { CarteCollaborator } from "./CarteCollaborator/CarteCollaborator";
import { BadgesCollaborator } from "./BadgesCollaborator/BadgesCollaborator";
import {
  useDisabledEmployee,
  useGetAllEmployeeWithVehicle,
  useGetOneEmployee,
  useReactivateEmployee,
} from "../../../api/employee";
import { getCurrentFleetId } from "../../../utils/getCurrentFleet";
import { columnsClaims } from "./fakeData";
type Props = {};

export const UpdateCollaborator: React.FC<Props> = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { id = "" } = useParams();
  const userId = parseInt(id);
  const currentFleet = getCurrentFleetId();

  // Fetch all collaborators for prev/next navigation
  const { employees: allEmployees } = useGetAllEmployeeWithVehicle(currentFleet);
  const sortedIds = (allEmployees ?? []).map((e: any) => e.userId ?? e.id).filter(Boolean);
  const currentIdx = sortedIds.indexOf(userId);
  const prevId = currentIdx > 0 ? sortedIds[currentIdx - 1] : null;
  const nextId = currentIdx >= 0 && currentIdx < sortedIds.length - 1 ? sortedIds[currentIdx + 1] : null;

  const [openDeactive, setOpenDeactive] = useState(false);
  const navigate = useNavigate();

  /*
   * The header button used to be a one-way switch: it always read "Deactivate"
   * and always called the deactivate endpoint, so a deactivated collaborator
   * looked identical to an active one and could never be brought back from
   * this page. The API returns company_user.is_active on this record and has
   * always exposed a matching /reactivate route; both were simply unused.
   *
   * is_active comes back from MySQL as the STRING "0" for a deactivated user,
   * which is truthy in JS - comparing it directly would have every collaborator
   * read as active. Number() is what makes this correct, and it is the same
   * comparison PermissionContext already uses for the membership flag.
   */
  const { employee } = useGetOneEmployee(currentFleet, userId);
  const isActive = Number(employee?.is_active ?? 1) === 1;

  const { mutate: disabledUser, isPending: isDisabling } = useDisabledEmployee(
    currentFleet,
    userId,
    () => setOpenDeactive(false)
  );

  const { mutate: reactivateUser, isPending: isReactivating } =
    useReactivateEmployee(currentFleet, userId, () => setOpenDeactive(false));

  const isPending = isDisabling || isReactivating;

  const onConfirmToggle = () => {
    if (isActive) {
      disabledUser();
    } else {
      reactivateUser();
    }
  };

  return (
    <>
      <CustomModal
        open={openDeactive}
        onCancel={() => setOpenDeactive(false)}
        title={t("collaborator.collaborator", "Collaborator")}
        onClose={() => setOpenDeactive(false)}
        onSave={() => toast.success(t("common.changeSaved", "Change saved"))}
        isConfirmation
        loadingSave={isPending}
        textConfirmation={
          isActive
            ? t("common.disabled", "Disabled")
            : t("common.activate", "Activate")
        }
        onConfirm={onConfirmToggle}
      >
        <div className={classes.textModalContainer}>
          <CustomTypography
            text={
              isActive
                ? t(
                    "collaborator.disableWarning",
                    "</br>You are about to deactivate the employee.</br></br>Do you confirm this action?"
                  )
                : t(
                    "collaborator.enableWarning",
                    "</br>You are about to reactivate the employee.</br>They will regain access to the fleet.</br></br>Do you confirm this action?"
                  )
            }
            variant="default"
          />
        </div>
      </CustomModal>
      <Sidebar
        styles={{
          sidebarRightRoot: classes.vehiculeRoot,
          sidebarRoot: classes.contentMain,
        }}
      >
        <div className={classes.container}>
          <div className={classes.containerHeader}>
            <div>
              <CustomTypography
                variant="h2"
                text={t("collaborator.collaborator", "Collaborator")}
                lineHeight={"47px"}
                isUppercase
              />
              <div className={classes.iconContainerHeader}>
                <Icon name={"collaborateur"} height={24} width={24} />
                <CustomTypography variant="default" text={`ID: ${userId}`} />
                <div style={{ display: "flex", gap: 4, marginLeft: 12 }}>
                  <button
                    disabled={!prevId}
                    onClick={() => prevId && navigate(`/collaborator/update/${prevId}`)}
                    style={{
                      border: "1px solid rgba(148,163,184,0.3)",
                      borderRadius: 6,
                      background: prevId ? "rgba(37,99,235,0.08)" : "transparent",
                      cursor: prevId ? "pointer" : "not-allowed",
                      opacity: prevId ? 1 : 0.35,
                      padding: "4px 8px",
                      display: "flex",
                      alignItems: "center",
                      fontSize: 13,
                      color: "inherit",
                    }}
                    title={t("common.previous", "Previous")}
                  >
                    ← {t("common.previous", "Préc.")}
                  </button>
                  <button
                    disabled={!nextId}
                    onClick={() => nextId && navigate(`/collaborator/update/${nextId}`)}
                    style={{
                      border: "1px solid rgba(148,163,184,0.3)",
                      borderRadius: 6,
                      background: nextId ? "rgba(37,99,235,0.08)" : "transparent",
                      cursor: nextId ? "pointer" : "not-allowed",
                      opacity: nextId ? 1 : 0.35,
                      padding: "4px 8px",
                      display: "flex",
                      alignItems: "center",
                      fontSize: 13,
                      color: "inherit",
                    }}
                    title={t("common.next", "Next")}
                  >
                    {t("common.next", "Suiv.")} →
                  </button>
                </div>
              </div>
            </div>
            <CustomButtonTable
              variant={"secondary"}
              textVariant={"link"}
              label={
                isActive
                  ? t(
                      "collaborator.deactivateTheCollaborator",
                      "Deactive the collaborator"
                    )
                  : t(
                      "collaborator.activateTheCollaborator",
                      "Activate the collaborator"
                    )
              }
              textTransform={"uppercase"}
              width={"fit-content"}
              fontWeight={"bold"}
              textDecoration={"none"}
              iconName={isActive ? "trash" : "check"}
              onClick={() => setOpenDeactive(true)}
            />
          </div>
          <div className={classes.subContainer}>
            <div className={classes.leftContent}>
              <ContainerCollaborator isEditableIcon fullInfos userId={userId} />
              <div className={classes.containerBodyLeft}>
                <div className={classes.divDocument}>
                  <DocumentsCollaborator />
                  <CarteCollaborator />
                  <BadgesCollaborator />
                </div>

                {/* sinistres */}
                <div className={classes.divClaims}>
                  <CustomTable
                    titleHeader={t("panel.claims.claims", "Claims")}
                    columns={columnsClaims}
                    data={[]}
                    labelButtonFooter={t(
                      "panel.claims.seeAllClaims",
                      "See all claims"
                    )}
                    showIconEye
                    tableMaxHeight={"357px"}
                    tableMinHeight={"357px"}
                    showPagination={false}
                    iconButton="plus"
                    onClickIconButton={() => navigate("/claims/create")}
                    onClickButtonFooter={() => navigate("/claims/list")}
                  />
                </div>
              </div>
            </div>
            <div className={classes.rightContent}>
              <VehiculeAffected />
              <HistoriqueContent />
            </div>
          </div>
        </div>
      </Sidebar>
    </>
  );
};
